import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';

import { FilterFieldDef } from './AddFilterModal.types';

export interface AddFilterFieldListProps {
  fields: FilterFieldDef[];
  category: string;
  recentlyUsed: string[];
  searchQuery: string;
  onSelect: (field: FilterFieldDef) => void;
}

const TYPE_LABELS: Record<string, string> = {
  list: 'List',
  text: 'Text',
  date: 'Date',
  number: 'Number',
  boolean: 'Yes/No',
};

export const AddFilterFieldList: React.FC<AddFilterFieldListProps> = ({
  fields,
  category,
  recentlyUsed,
  searchQuery,
  onSelect,
}) => {
  const theme = useTheme();

  const filtered = useMemo(() => {
    let list = category === 'All' ? fields : fields.filter((f) => f.category === category);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((f) => f.name.toLowerCase().includes(q));
    }
    return list;
  }, [fields, category, searchQuery]);

  const recentFields = useMemo(
    () => filtered.filter((f) => recentlyUsed.includes(f.key)),
    [filtered, recentlyUsed]
  );

  const groupedByCategory = useMemo(() => {
    if (category !== 'All') return null;
    const groups: Record<string, FilterFieldDef[]> = {};
    filtered.forEach((f) => {
      if (!groups[f.category]) groups[f.category] = [];
      groups[f.category].push(f);
    });
    return groups;
  }, [category, filtered]);

  const renderField = (field: FilterFieldDef) => (
    <ListItemButton
      key={field.key}
      onClick={() => onSelect(field)}
      sx={{
        py: 0.75,
        px: 2,
        '&:hover': { bgcolor: theme.palette.action.hover },
      }}
    >
      <ListItemText
        primary={field.name}
        slotProps={{ primary: { variant: 'body2' } }}
      />
      <Chip
        label={TYPE_LABELS[field.type] || field.type}
        size="small"
        sx={{
          height: 20,
          fontSize: '0.7rem',
          bgcolor: theme.palette.v4.filterInput,
          color: theme.palette.text.secondary,
        }}
      />
    </ListItemButton>
  );

  return (
    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      {filtered.length === 0 && (
        <Typography
          variant="body2"
          sx={{ p: 3, color: theme.palette.text.secondary, textAlign: 'center' }}
        >
          No fields found
        </Typography>
      )}

      {recentFields.length > 0 && (
        <>
          <Typography
            variant="caption"
            sx={{
              px: 2,
              pt: 1.5,
              pb: 0.5,
              display: 'block',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: theme.palette.text.secondary,
            }}
          >
            Recently Used
          </Typography>
          <List dense disablePadding>
            {recentFields.map(renderField)}
          </List>
          <Divider sx={{ my: 0.5 }} />
        </>
      )}

      {groupedByCategory
        ? Object.entries(groupedByCategory).map(([cat, catFields]) => (
            <React.Fragment key={cat}>
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  pt: 1.5,
                  pb: 0.5,
                  display: 'block',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: theme.palette.text.secondary,
                }}
              >
                {cat}
              </Typography>
              <List dense disablePadding>
                {catFields.map(renderField)}
              </List>
            </React.Fragment>
          ))
        : (
          <List dense disablePadding>
            {filtered
              .filter((f) => !recentFields.some((r) => r.key === f.key))
              .map(renderField)}
          </List>
        )}
    </Box>
  );
};
